import type { BuildChartContext, ChartData, ChartViewModel } from '../catalog/types';
import { ChartType } from 'shared/types/analytics';
import { useContext, type ReactElement } from 'react';
import { ChartDetailsContext } from '../context/chartDetailsContext';
import type { ChartId } from '../catalog/chartCatalog';
import { prepareDonutDisplayData } from '../model/prepareDonutDisplayData';
import { DonutChartView } from './DonutChartView';
import { BarChartView } from './BarChartView';
import { LineChartView } from './LineChartView';
import { TripletChartView } from './TripletChartView';
import { MemoEmptyChart } from 'charts/common/EmptyChart';
import { resolveChartSeriesColors } from '../theme/resolveChartColors';

type MuiChartRendererProps = Readonly<{
  chartId: ChartId;
  viewModel: ChartViewModel;
  data: ChartData;
  ctx: BuildChartContext;
  height?: number;
}>;

export function MuiChartRenderer({
  chartId,
  viewModel,
  data,
  ctx,
  height,
}: MuiChartRendererProps): ReactElement {
  const isDetailedView = useContext(ChartDetailsContext);
  const chartHeight = height ?? ctx.tokens.heights[ctx.variant];

  if (viewModel.isEmpty) {
    return <MemoEmptyChart variant={ctx.variant} />;
  }

  if (viewModel.type === ChartType.triplet) {
    return (
      <TripletChartView
        chartId={chartId}
        values={data.tripletValues ?? []}
      />
    );
  }

  const baseColors = ctx.colors.series;

  switch (viewModel.type) {
    case ChartType.donut: {
      const displayData = prepareDonutDisplayData(
        data,
        ctx.intl,
        !isDetailedView
      );
      const colors = resolveChartSeriesColors(
        chartId,
        baseColors,
        displayData.values.length
      );
      return (
        <DonutChartView
          chartId={chartId}
          data={displayData}
          height={chartHeight}
          colors={colors}
          intl={ctx.intl}
          variant={ctx.variant}
        />
      );
    }
    case ChartType.bar: {
      const colors = resolveChartSeriesColors(
        chartId,
        baseColors,
        data.values.length
      );
      return (
        <BarChartView
          chartId={chartId}
          data={data}
          height={chartHeight}
          colors={colors}
          variant={ctx.variant}
        />
      );
    }
    case ChartType.line:
      return (
        <LineChartView
          data={data}
          height={chartHeight}
          colors={resolveChartSeriesColors(chartId, baseColors)}
          variant={ctx.variant}
        />
      );
    default:
      return <MemoEmptyChart variant={ctx.variant} />;
  }
}
